/**
 * Client-side lease calculation using PMT formula.
 * Ported from class-car-form-api.php.
 */

const INTEREST_RATE_ZAKELIJK = 8.99;
const INTEREST_RATE_PARTICULIER = 7.99;

/**
 * PMT formula (Excel BET equivalent).
 *
 * @param {number} rate Monthly interest rate as decimal (e.g., 0.00749 for 8.99%/12).
 * @param {number} nper Number of periods (months).
 * @param {number} pv   Present value (negative loan amount).
 * @param {number} fv   Future value (residual value, default 0).
 * @param {number} type Payment type: 0 = end of period, 1 = beginning (default 0).
 * @returns {number} Monthly payment amount.
 */
export function pmt( rate, nper, pv, fv = 0, type = 0 ) {
	if ( rate === 0 ) {
		return -( pv + fv ) / nper;
	}
	const pvif = Math.pow( 1 + rate, nper );
	let pmtVal = -( rate * ( pv * pvif + fv ) ) / ( pvif - 1 );
	if ( type === 1 ) {
		pmtVal = pmtVal / ( 1 + rate );
	}
	return pmtVal;
}

/**
 * Apply rounding rules for 72 months zakelijk lease.
 * Decimalen 0.00-0.49: rond naar beneden, 0.50+: rond naar boven.
 *
 * @param {number} amount Amount to round.
 * @returns {number} Rounded amount.
 */
export function applyRoundingRules72Months( amount ) {
	const decimals = amount - Math.floor( amount );
	if ( decimals >= 0.50 ) {
		return Math.ceil( amount );
	}
	return Math.floor( amount );
}

/**
 * Calculate maandbedrag for standalone lease calculator.
 *
 * @param {Object} params Calculation parameters.
 * @param {number} params.aankoopbedrag Purchase price (net).
 * @param {number} params.looptijd Term in months.
 * @param {number} params.aanbetaling Down payment.
 * @param {number} params.slottermijn Residual value.
 * @param {number} params.type 0 = Zakelijk, 1 = Particulier.
 * @returns {number} Monthly payment amount.
 */
export function calculateMaandbedrag( params ) {
	const {
		aankoopbedrag = 0,
		looptijd = 72,
		aanbetaling = 0,
		slottermijn = 0,
		type = 0,
	} = params;

	const netPrice = Number( aankoopbedrag ) || 0;
	const aanbetalingNum = Number( aanbetaling ) || 0;
	const slottermijnNum = Number( slottermijn ) || 0;
	const looptijdInt = Math.max( 1, Math.floor( Number( looptijd ) ) || 72 );
	const typeInt = type === 1 ? 1 : 0;

	if ( netPrice <= 0 ) {
		return 0;
	}

	const annualRate = typeInt === 1 ? INTEREST_RATE_PARTICULIER : INTEREST_RATE_ZAKELIJK;
	const monthlyRate = ( annualRate / 12 ) / 100;
	const kredietbedrag = Math.max( 0, netPrice - aanbetalingNum );

	let maandbedrag = pmt( monthlyRate, looptijdInt, -kredietbedrag, slottermijnNum, 0 );

	if ( typeInt === 0 && looptijdInt === 72 ) {
		maandbedrag = applyRoundingRules72Months( maandbedrag );
		maandbedrag = maandbedrag + 10;
	} else {
		maandbedrag = maandbedrag + 10;
	}

	return Math.max( 0, Math.round( maandbedrag * 100 ) / 100 );
}
